import {skipToken} from '@reduxjs/toolkit/query'
import {memo, useCallback, useEffect} from 'react'

import {changeInvestigation, openDialog} from '../../../actions'
import {useBranch} from '../../../hooks/routes'
import useStatusSubscription from '../../../hooks/useStatusSubscription'
import {getInvestigationArg} from '../../../rest/investigations'
import {getBuildType} from '../../../selectors'
import {restApi} from '../../../services/rest'
import type {BuildTypeId} from '../../../types'
import {DialogType, stringifyId} from '../../../types'
import {emptyArray} from '../../../utils/empty'
import {subscribeOnBuildTypeEvents} from '../../../utils/subscriber'
import * as SubscriptionEvents from '../../../utils/subscriptionEvents'
import BuildTypeInvestigationHistoryPopup from '../../packages/InvestigationHistory/BuildTypeInvestigationHistoryPopup/BuildTypeInvestigationHistoryPopup'
import {useSequenceLoaderReadyHandler} from '../SequenceLoader/SequenceLoader.hooks'
import type {SeqProps} from '../SequenceLoader/SequenceLoader.types'

import Investigations from './Investigations'
import {getIsBuildTypeFailure} from './Investigations.selectors'
import {getActiveInvestigations} from './Investigations.utils'

import {useAppDispatch, useAppSelector} from 'src/hooks/react-redux'

const throttleTime = 0

type Props = SeqProps & {
  className?: string
  iconClassName?: string
  buildTypeId: BuildTypeId | null | undefined
  isShowOnlyIcon?: boolean
}

function BuildTypeInvestigations(props: Props) {
  const dispatch = useAppDispatch()
  const {className, iconClassName, buildTypeId, isShowOnlyIcon} = props

  const {projectId, name, internalId} =
    useAppSelector(state => getBuildType(state, buildTypeId)) ?? {}

  useStatusSubscription({buildTypeId, projectId})

  const branch = useBranch()

  const {investigations, investigationsReady, refetch} =
    restApi.endpoints.getAllInvestigations.useQuery(
      buildTypeId != null ? getInvestigationArg(buildTypeId) : skipToken,
      {
        selectFromResult: ({data}) => ({
          investigations: data?.investigation ?? emptyArray,
          investigationsReady: data != null,
        }),
      },
    )
  useSequenceLoaderReadyHandler(investigationsReady)
  const isBuildTypeFailure = useAppSelector(state =>
    getIsBuildTypeFailure(state, buildTypeId, branch),
  )

  const onAssignInvestigation = useCallback(
    (presetFix = false) => {
      if (buildTypeId != null && name != null) {
        dispatch(changeInvestigation(buildTypeId, name, presetFix, refetch))
      }
    },

    [buildTypeId, name, dispatch, refetch],
  )
  const onShowInvestigationHistory = useCallback(
    () =>
      buildTypeId != null &&
      dispatch(openDialog(stringifyId(buildTypeId), DialogType.INVESTIGATION_HISTORY)),
    [buildTypeId, dispatch],
  )

  useEffect(
    () =>
      buildTypeId != null && internalId != null
        ? subscribeOnBuildTypeEvents(
            internalId,
            [SubscriptionEvents.BUILD_TYPE_RESPONSIBILITY_CHANGES],
            refetch,
            throttleTime,
          )
        : undefined,
    [dispatch, buildTypeId, internalId, refetch],
  )

  const currentlyInvestigated = getActiveInvestigations(investigations).length > 0

  return isBuildTypeFailure || currentlyInvestigated ? (
    <>
      <Investigations
        className={className}
        iconClassName={iconClassName}
        projectId={projectId}
        buildTypeId={buildTypeId}
        investigations={investigations}
        currentlyInvestigated={currentlyInvestigated}
        isShowOnlyIcon={isShowOnlyIcon}
        onAssignInvestigation={onAssignInvestigation}
        onShowInvestigationHistory={onShowInvestigationHistory}
      />
      <BuildTypeInvestigationHistoryPopup buildTypeId={buildTypeId} id={buildTypeId} />
    </>
  ) : null
}

export default memo(BuildTypeInvestigations)
